import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Camera, 
  Upload, 
  Trash2, 
  User, 
  ArrowLeft,
  Loader2,
  CheckCircle,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isInfluencer, loading: authLoading, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [localAvatar, setLocalAvatar] = useState(null);

  // Redirect if not authenticated or not influencer
  if (!authLoading && !isAuthenticated) {
    navigate('/login');
    return null;
  }
  if (!authLoading && isAuthenticated && !isInfluencer) {
    navigate('/');
    return null;
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/profile/upload-avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setLocalAvatar(response.data.avatar_url);
      toast.success('Profile picture uploaded successfully!');
      // Refresh user data in context
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setAvatarPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!currentAvatar) return;

    try {
      await axios.delete(`${API_URL}/profile/avatar`);
      setLocalAvatar(null);
      setAvatarPreview(null);
      toast.success('Profile picture removed');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to remove profile picture');
    }
  };

  const currentAvatar = localAvatar || avatarPreview || user?.avatar_url;

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" className="gap-2 mb-4" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <h1 className="font-heading font-bold text-3xl">Profile Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your creator profile</p>
        </div>

        {/* Profile Picture Section */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Profile Picture
            </CardTitle>
            <CardDescription>
              Upload a photo to personalize your profile. Recommended: Square image, at least 200x200px.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Avatar Preview */}
              <div className="relative group">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/50 to-primary/20 flex items-center justify-center text-4xl font-heading font-bold text-white border-4 border-primary/30 overflow-hidden">
                  {currentAvatar ? (
                    <img 
                      src={currentAvatar} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-white/70" />
                  )}
                </div>
                
                {/* Upload overlay */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </button>
              </div>

              {/* Upload Controls */}
              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="avatar-file-input"
                />
                
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-avatar-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload New Photo
                    </>
                  )}
                </Button>

                {currentAvatar && (
                  <Button 
                    variant="outline" 
                    onClick={handleDeleteAvatar}
                    className="gap-2 text-destructive hover:text-destructive"
                    data-testid="delete-avatar-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove Photo
                  </Button>
                )}

                <p className="text-xs text-muted-foreground">
                  Supports: JPEG, PNG, WebP, GIF (max 5MB)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Info Section */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Display Name</Label>
              <Input value={user?.display_name || ''} disabled className="mt-1 bg-secondary/50" />
            </div>
            
            <div>
              <Label>Username</Label>
              <Input value={user?.username || ''} disabled className="mt-1 bg-secondary/50" />
              <p className="text-xs text-muted-foreground mt-1">
                Your public profile URL: fanbound.com/{user?.username}
              </p>
            </div>

            <div>
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="mt-1 bg-secondary/50" />
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="bg-card border-white/10">
          <CardHeader>
            <CardTitle>Quick Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {user?.username && (
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2"
                onClick={() => navigate(`/${user.username}`)}
              >
                <ExternalLink className="w-4 h-4" />
                View Public Profile
              </Button>
            )}
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2"
              onClick={() => navigate('/dashboard')}
            >
              <CheckCircle className="w-4 h-4" />
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
